import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, User, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${window.location.origin}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // CAPTCHA state (server-issued challenge, validated server-side)
  const [captcha, setCaptcha] = useState(null);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const refreshCaptcha = async (signal) => {
    setCaptchaInput("");
    setCaptchaError("");
    try {
      const response = await axios.get(`${API}/captcha/challenge`, { signal });
      setCaptcha(response.data);
    } catch (e) {
      if (!axios.isCancel(e)) setCaptcha(null);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    refreshCaptcha(controller.signal);
    return () => controller.abort();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCaptchaError("");

    if (!captcha || !captchaInput) {
      setCaptchaError("Please answer the verification question.");
      return;
    }

    setLoading(true);

    const result = await login(formData.username, formData.password, captcha.captcha_id, parseInt(captchaInput));

    if (result.success) {
      navigate("/admin");
    } else {
      setError(result.error || "Invalid username or password");
      setLoading(false);
      refreshCaptcha(); // Challenge is one-time use - fetch a new one
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back to Home Button */}
        <button
          onClick={() => navigate("/")}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold logo-text-small mb-2">FIREDL</h1>
            <p className="text-gray-600">Admin Login</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="username" className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4" />
                Username
              </Label>
              <Input
                id="username"
                type="text"
                data-testid="admin-username-input"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                placeholder="Enter your username"
                required
                className="w-full"
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="admin-password-input"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                placeholder="Enter your password"
                required
                className="w-full"
              />
            </div>

            {/* CAPTCHA Verification */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <Label htmlFor="captcha" className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                <span className="font-semibold">Verify you're not a robot</span>
              </Label>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-white border-2 border-gray-300 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-gray-800" data-testid="admin-captcha-question">
                      What is {captcha ? captcha.num1 : "…"} + {captcha ? captcha.num2 : "…"}?
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Refresh CAPTCHA"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
                
                <Input
                  id="captcha"
                  type="number"
                  data-testid="admin-captcha-input"
                  value={captchaInput}
                  onChange={(e) => {
                    setCaptchaInput(e.target.value);
                    setCaptchaError("");
                  }}
                  placeholder="Enter your answer"
                  required
                  className="w-full"
                />
                
                {captchaError && (
                  <p className="text-sm text-red-600">
                    {captchaError}
                  </p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
